import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { colorTextMuted, colorTextPrimary } from "src/design/tokens";
import { useRenewSubscription } from "src/data/admin/queries/subscription";
import { AdminRestaurantSubscriptionDto } from "src/data/admin/types/subscription";

interface RenewSubscriptionDialogProps {
  /** The subscription to renew; `null` keeps the dialog closed. */
  subscription: AdminRestaurantSubscriptionDto | null;
  onClose: () => void;
  onRenewed: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * The renewal starts from the current expiration when it has not run out yet —
 * nothing is lost by renewing early — and from today once it has. Mirrors the
 * backend so the preview matches what will be stored.
 */
const getRenewedExpiration = (
  subscription: AdminRestaurantSubscriptionDto,
  months: number,
) => {
  const today = dayjs().startOf("day");
  const current = subscription.nextBillingCycle
    ? dayjs(subscription.nextBillingCycle)
    : null;
  const renewFrom =
    current === null || current.isBefore(today) ? today : current;

  return renewFrom.add(months, "month");
};

export const RenewSubscriptionDialog = ({
  subscription,
  onClose,
  onRenewed,
  onError,
}: RenewSubscriptionDialogProps) => {
  const [months, setMonths] = useState("1");

  useEffect(() => {
    if (subscription) setMonths("1");
  }, [subscription]);

  const renewMutation = useRenewSubscription({
    onSuccess: (renewed) => {
      onRenewed(
        `${renewed.restaurantName} now expires on ${dayjs(
          renewed.nextBillingCycle,
        ).format("MMM D, YYYY")}.`,
      );
      onClose();
    },
  });

  const monthsValue = Number(months);
  const isValid = Number.isInteger(monthsValue) && monthsValue >= 1;
  const preview =
    subscription && isValid
      ? getRenewedExpiration(subscription, monthsValue)
      : null;

  const handleSubmit = () => {
    if (!subscription || !isValid) return;
    renewMutation.mutate(
      { restaurantId: subscription.restaurantId, months: monthsValue },
      {
        onError: (error) =>
          onError(
            error instanceof Error
              ? error.message
              : "Failed to renew the subscription",
          ),
      },
    );
  };

  return (
    <Dialog
      open={!!subscription}
      onClose={() => !renewMutation.isPending && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Renew {subscription?.restaurantName}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="number"
          label="Months"
          value={months}
          onChange={(event) => setMonths(event.target.value)}
          inputProps={{ min: 1, step: 1 }}
          error={!isValid}
          helperText={
            isValid ? undefined : "Must be a whole number of at least 1"
          }
          sx={{ mt: 1 }}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: colorTextMuted }}>
            {subscription?.expired
              ? "This subscription has run out, so the renewal starts from today."
              : "Renewing early keeps the remaining time — the renewal starts from the current expiration."}
          </Typography>
          {preview && (
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: 600, color: colorTextPrimary }}
            >
              New expiration: {preview.format("MMM D, YYYY")}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={renewMutation.isPending}
        >
          Cancel
        </CustomButton>
        <CustomLoadingButton
          loading={renewMutation.isPending}
          disabled={!isValid}
          onSubmit={handleSubmit}
          label="Renew"
          color="success"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
